import React from 'react';

import { useSelector } from 'react-redux';

import { userSelector } from '../../../reducers/current-user-selectors';
import { jobsForOrgIdSelector } from '../../../reducers/job-selectors';

import { ApplicantMessageTemplateParser } from '../../../message-templates/parsers/applicant-message-template-parser';

import { ApplicantAutomationDialog } from './applicant-automation-dialog';
import { EmployeeAutomationDialog } from './employee-automation-dialog';

import { AutomationsByTriggerDictionary } from '../../utils/applicant-automation-display-utils';
import { isApplicantAutomationOrCopy } from '../../utils/is-applicant-automation';

type InputProps = {
  copiedAutomation: ApplicantAutomation | StartDateTriggeredCommunication;
  title: string;
  editingForJob?: Job;
  org: Org;
  onClose: () => void;
  applicantAutomationSection?: ApplicantAutomationSection;
  parentAutomationsForSection?: AutomationsByTriggerDictionary;
};

export const CopyAutomationDialog = ({
  copiedAutomation,
  title,
  editingForJob,
  org,
  onClose,
  applicantAutomationSection,
  parentAutomationsForSection,
}: InputProps) => {
  const user = useSelector((state: any) => userSelector(state));
  const jobs = useSelector((state: any) => jobsForOrgIdSelector(state, org.org_id));

  if (!copiedAutomation) {
    return null;
  }

  if (isApplicantAutomationOrCopy(copiedAutomation)) {
    const parserJob = editingForJob || (jobs || [])[0];

    if (!applicantAutomationSection) {
      throw new Error('Section needed for applicant automations');
    }

    const parser = new ApplicantMessageTemplateParser({
      useStandInApplicant: true,
      ...(parserJob ? { job: parserJob } : { org, useStandInJob: true }),
      user,
    });

    const { applicantTriggeredCommunicationId, ...automationToCopy } = copiedAutomation;
    const copiedAutomationProps = { ...automationToCopy };

    return (
      <ApplicantAutomationDialog
        key={`copy-automation-${applicantTriggeredCommunicationId}`}
        parser={parser}
        automation={copiedAutomationProps}
        onClose={onClose}
        fallbackDescription={title}
        orgName={org.name}
        orgId={org.org_id}
        showBulkUpdate={!!copiedAutomationProps.defaultForOrgId}
        section={applicantAutomationSection}
        editingForJob={editingForJob}
        parentAutomationsForSection={parentAutomationsForSection}
        copiedFromAutomationId={applicantTriggeredCommunicationId}
      />
    );
  }

  const copiedAutomationProps = { ...copiedAutomation } as StartDateTriggeredCommunicationCopy;
  delete copiedAutomationProps.startDateTriggeredCommunicationId;

  const { StartDateTriggeredAlert, StartDateTriggeredMessage, EmployeeAutomationRecipients } =
    copiedAutomation;

  // Clone nested objects
  copiedAutomationProps.EmployeeAutomationRecipients = [...EmployeeAutomationRecipients];
  if (StartDateTriggeredAlert) {
    copiedAutomationProps.StartDateTriggeredAlert = { ...StartDateTriggeredAlert };
    delete copiedAutomationProps.startDateTriggeredAlertId;
    delete copiedAutomationProps.StartDateTriggeredAlert?.startDateTriggeredAlertId;
  }
  if (StartDateTriggeredMessage) {
    copiedAutomationProps.StartDateTriggeredMessage = { ...StartDateTriggeredMessage };
    delete copiedAutomationProps.startDateTriggeredMessageId;
    delete copiedAutomationProps.StartDateTriggeredMessage?.startDateTriggeredMessageId;
  }

  return (
    <EmployeeAutomationDialog
      key={`copy-automation-${copiedAutomation.startDateTriggeredCommunicationId}`}
      org={org}
      triggerType={copiedAutomationProps.triggerType}
      onClose={onClose}
      editingCommunication={copiedAutomationProps}
      copiedFromAutomationId={copiedAutomation.startDateTriggeredCommunicationId}
    />
  );
};
